import React from 'react';
import {
  C, td, fmtNum, fmtUsd, fmtAnnualPct,
  renderRegime, renderTickers, renderSector, renderMarketCap, renderCountry, metaTooltip, rowMeta,
} from './shared';

const FAV_BG = '#FAEEDA';
const FAV_BORDER = '3px solid #F59E0B';

function stickyTd(left, extra = {}) {
  return {
    ...td,
    position: 'sticky',
    left,
    zIndex: 1,
    ...extra,
  };
}

function badge(color) {
  return {
    display: 'inline-block', padding: '2px 7px', borderRadius: 4, fontSize: 11, fontWeight: 600,
    background: color === 'green' ? '#DCFCE7' : color === 'red' ? '#FEE2E2' : '#F1F5F9',
    color: color === 'green' ? C.success : color === 'red' ? C.danger : C.textMd,
  };
}

export default function TableRow({
  r, idx, pageOffset,
  isChecked, isStarred,
  visibleColumns,
  sortCol,
  periodDefs = [],
  hasChainSummary = false,
  onCheck, onStar, onView, onCopy, onRunMock, onRecalc, onDelete,
  onOpenPeriods,
  recalcingId,
  runningBotNames = [],
}) {
  const p = r.params || {};
  const m = r.metrics || {};
  // Мета батчу + набір капіталізації саме цього рядка (вісь перебору).
  const rm = rowMeta(r._meta, p);
  // Headline metrics show the across-periods aggregate when the row has periods
  // (the main period is one of them, detailed in the popup); else the main sim.
  // Matches the server-side COALESCE sort so display and ordering agree.
  const _pa = r.period_aggregates || {};
  // Список більше не возить сам масив вікон — лише лічильники (periods_summary).
  // `periods_results` лишається запасним шляхом: так само читаються рядки,
  // оновлені попапом на місці, і будь-яка відповідь, віддана до цієї зміни.
  const _ps = r.periods_summary || null;
  const _prArr = Array.isArray(r.periods_results) ? r.periods_results : [];
  const _hasPeriods = (_ps ? _ps.total : _prArr.length) > 0;
  const eff = _hasPeriods ? {
    win_rate:              _pa.win_rate ?? m.win_rate,
    monthly_return:        _pa.avg_monthly_return ?? m.monthly_return,
    yearly_return:         _pa.avg_yearly_return ?? m.yearly_return,
    sharpe_ratio:          _pa.pooled_sharpe ?? m.sharpe_ratio,
    max_drawdown:          _pa.worst_max_drawdown ?? m.max_drawdown,
    calmar_ratio:          _pa.worst_calmar ?? m.calmar_ratio,
    profitable_months_pct: _pa.pooled_profitable_months_pct ?? m.profitable_months_pct,
    worst_month:           _pa.pooled_worst_month ?? m.worst_month,
  } : m;
  const isRunning = r.status === 'running';
  const _mockLabel = r.name || (r.id != null ? `Strategy #${r.id}` : null);
  const isMockRunning = _mockLabel != null && runningBotNames.includes(_mockLabel);

  const bgColor = isChecked ? '#EFF6FF' : isStarred ? FAV_BG : '';
  const stickyBg = isChecked ? '#EFF6FF' : isStarred ? FAV_BG : C.cardBg;

  const rowStyle = {
    cursor: isRunning ? 'default' : 'pointer',
    background: bgColor,
    transition: 'background 0.1s',
  };

  const checkboxCellStyle = {
    ...stickyTd(0, { width: 36, textAlign: 'center', padding: '8px 6px', background: stickyBg }),
    borderLeft: isStarred ? FAV_BORDER : `1px solid ${C.border}`,
  };

  const numCellStyle = stickyTd(36, {
    width: 50, textAlign: 'center',
    color: C.textSm, fontSize: 11, fontWeight: 600,
    background: stickyBg,
  });

  const nameCellStyle = { ...td, minWidth: 200, fontWeight: 500 };

  const btnSm = {
    padding: '4px 8px', background: 'transparent', color: C.primary,
    border: `1px solid ${C.border}`, borderRadius: 5, fontSize: 11, cursor: 'pointer',
  };

  return (
    <tr
      style={rowStyle}
      onClick={() => !isRunning && onView(r)}
      onMouseEnter={e => { if (!isChecked && !isStarred) e.currentTarget.style.background = '#F0F9FF'; }}
      onMouseLeave={e => { e.currentTarget.style.background = bgColor; }}
    >
      {/* ── Sticky columns ── */}
      <td style={checkboxCellStyle} onClick={e => { e.stopPropagation(); onCheck(r.id); }}>
        <input type="checkbox" checked={isChecked} readOnly style={{ cursor: 'pointer', accentColor: C.primary }} />
      </td>

      <td style={numCellStyle} title={`DB ID: ${r.id}`}>
        #{r.id}
      </td>

      <td style={{ ...td, width: 38, textAlign: 'center', padding: '8px 4px', fontSize: 16 }}
        onClick={e => { e.stopPropagation(); onStar(r.id); }}
        title={isStarred ? 'Remove from favorites' : 'Add to favorites'}
      >
        <span style={{ color: isStarred ? '#F59E0B' : '#CBD5E1', cursor: 'pointer', userSelect: 'none' }}>
          {isStarred ? '★' : '☆'}
        </span>
      </td>

      <td style={nameCellStyle} title={r.status === 'failed' && r.error_detail ? r.error_detail : undefined}>
        {r.name || '—'}
        {(() => {
          const eng = (r._meta?.engine || 'numba').toLowerCase();
          const isDefault = eng === 'numba';
          return (
            <span style={{
              display: 'inline-block', padding: '1px 6px', borderRadius: 4,
              fontSize: 10, fontWeight: 600, marginLeft: 6, verticalAlign: 'middle',
              background: isDefault ? '#E2E8F0' : eng === 'python' ? '#FEF3C7' : '#DBEAFE',
              color:      isDefault ? '#64748B' : eng === 'python' ? '#92400E' : '#1E40AF',
            }}>{eng}</span>
          );
        })()}
        {r.status === 'failed' && r.error_detail && (
          <span style={{ marginLeft: 6, fontSize: 10, color: '#EF4444', fontWeight: 400 }}>
            — {r.error_detail.length > 60 ? r.error_detail.slice(0, 60) + '…' : r.error_detail}
          </span>
        )}
      </td>

      <td style={{ ...td, fontSize: 11, color: C.textSm, whiteSpace: 'nowrap' }}>
        {r.updated_at ? r.updated_at.slice(0, 16).replace('T', ' ') : '—'}
      </td>

      {/* ── Scrollable columns ── */}
      {visibleColumns.buy_pct  && <td style={td}>{p.x_buy != null ? p.x_buy + '%' : '—'}</td>}
      {visibleColumns.t_buy    && <td style={td}>{p.t_hours_buy ?? '—'}</td>}
      {visibleColumns.intraday && (
        <td style={{ ...td, textAlign: 'center' }}>
          {p.intraday_enabled
            ? <span style={{ fontSize: 11, color: C.primary, fontWeight: 600 }}>✓ {p.intraday_pct}%</span>
            : <span style={{ color: C.textSm }}>—</span>}
        </td>
      )}
      {visibleColumns.drop_pct && <td style={td}>{p.y_sell != null ? p.y_sell + '%' : '—'}</td>}
      {visibleColumns.t_sell   && <td style={td}>{p.sell_window_days != null ? p.sell_window_days : '—'}</td>}
      {visibleColumns.n_cos    && <td style={td}>{p.num_companies ?? '—'}</td>}
      {visibleColumns.pos_pct  && <td style={td}>{p.pos_size != null ? fmtNum(p.pos_size, 1) + '%' : '—'}</td>}
      {visibleColumns.capital  && <td style={td}>{p.initial_capital ? fmtUsd(p.initial_capital) : '—'}</td>}

      {visibleColumns.win_rate && (
        <td style={td}>
          {eff.win_rate != null
            ? <span style={badge(eff.win_rate >= 60 ? 'green' : 'red')}>{eff.win_rate.toFixed(1)}%</span>
            : '—'}
        </td>
      )}

      {visibleColumns.monthly  && (
        <td style={{ ...td, background: sortCol === 'monthly_return' ? '#EFF6FF' : undefined }}>
          {eff.monthly_return != null
            ? <span style={{ fontWeight: 700, color: eff.monthly_return >= 0 ? C.success : C.danger }}>
                {fmtAnnualPct(eff.monthly_return, 2)}
              </span>
            : '—'}
        </td>
      )}

      {visibleColumns.yearly   && (
        <td style={td}>
          {eff.yearly_return != null
            ? <span style={{ fontWeight: 600, color: eff.yearly_return >= 0 ? C.success : C.danger }}>
                {fmtAnnualPct(eff.yearly_return, 1)}
              </span>
            : '—'}
        </td>
      )}

      {visibleColumns.worst_yearly_periods && (
        <td style={{...td, background: sortCol === 'worst_yearly_return_periods' ? '#EFF6FF' : undefined}}>
          {r.worst_yearly_return_periods != null
            ? <span style={{ fontWeight: 600, color: r.worst_yearly_return_periods >= 0 ? C.success : C.danger }}>
                {fmtAnnualPct(r.worst_yearly_return_periods, 1)}
              </span>
            : '—'}
        </td>
      )}
      {/* Частки вікон за знаком. Колонка порожня («—»), поки рядок не перевірено
          на додаткових вікнах: одне головне вікно не дає частки за побудовою. */}
      {visibleColumns.neg_yearly_share_periods && (() => {
        const v = r.neg_yearly_share_periods ?? _pa.neg_yearly_share;
        return (
          <td style={{...td, background: sortCol === 'neg_yearly_share_periods' ? '#EFF6FF' : undefined}}>
            {v != null
              ? <span style={{ fontWeight: 600, color: v > 0 ? C.danger : C.success }}>
                  {fmtNum(v, 1)}%
                </span>
              : '—'}
          </td>
        );
      })()}
      {visibleColumns.neg_yearly_avg_periods && (() => {
        const v = r.neg_yearly_avg_periods ?? _pa.neg_yearly_avg;
        return (
          <td style={{...td, background: sortCol === 'neg_yearly_avg_periods' ? '#EFF6FF' : undefined}}>
            {v != null
              ? <span style={{ fontWeight: 600, color: C.danger }}>{fmtAnnualPct(v, 1)}</span>
              : '—'}
          </td>
        );
      })()}
      {visibleColumns.best_yearly_periods && (() => {
        const best = r.best_yearly_return_periods ?? _pa.best_yearly_return;
        return (
          <td style={{...td, background: sortCol === 'best_yearly_return_periods' ? '#EFF6FF' : undefined}}>
            {best != null
              ? <span style={{ fontWeight: 600, color: best >= 0 ? C.success : C.danger }}>
                  {fmtAnnualPct(best, 1)}
                </span>
              : '—'}
          </td>
        );
      })()}
      {visibleColumns.pos_yearly_share_periods && (() => {
        const v = r.pos_yearly_share_periods ?? _pa.pos_yearly_share;
        return (
          <td style={{...td, background: sortCol === 'pos_yearly_share_periods' ? '#EFF6FF' : undefined}}>
            {v != null
              ? <span style={{ fontWeight: 600, color: v >= 100 ? C.success : C.textMd }}>
                  {fmtNum(v, 1)}%
                </span>
              : '—'}
          </td>
        );
      })()}
      {visibleColumns.pos_yearly_avg_periods && (() => {
        const v = r.pos_yearly_avg_periods ?? _pa.pos_yearly_avg;
        return (
          <td style={{...td, background: sortCol === 'pos_yearly_avg_periods' ? '#EFF6FF' : undefined}}>
            {v != null
              ? <span style={{ fontWeight: 600, color: C.success }}>{fmtAnnualPct(v, 1)}</span>
              : '—'}
          </td>
        );
      })()}
      {visibleColumns.best_worst_yearly_periods && (() => {
        const best  = r.best_yearly_return_periods ?? _pa.best_yearly_return;
        const worst = r.worst_yearly_return_periods ?? _pa.worst_yearly_return;
        const spread = (best != null && worst != null) ? (best - worst) : null;
        return (
          <td style={{...td, background: sortCol === 'best_minus_worst_yearly_periods' ? '#EFF6FF' : undefined}}>
            {spread != null
              ? <span style={{ fontWeight: 600, color: C.textMd }}>{fmtNum(spread, 1)}%</span>
              : '—'}
          </td>
        );
      })()}
      {visibleColumns.avg_yearly_periods && (() => {
        const avg = r.avg_yearly_return_periods ?? _pa.avg_yearly_return ?? eff.yearly_return;
        return (
          <td style={{...td, background: sortCol === 'avg_yearly_return_periods' ? '#EFF6FF' : undefined}}>
            {avg != null
              ? <span style={{ fontWeight: 600, color: avg >= 0 ? C.success : C.danger }}>
                  {fmtAnnualPct(avg, 1)}
                </span>
              : '—'}
          </td>
        );
      })()}
      {visibleColumns.avg_deals_periods && (() => {
        // Скільки угод на рік. Число рахує бекенд по врахованих вікнах
        // (колонка avg_deals_per_year_periods — за нею ж сортує сервер).
        // Рядок без додаткових вікон такої колонки не має: там угоди беруться
        // з головного прогону і приводяться до року довжиною його вікна —
        // тим самим відкатом, що й у сусідній Avg. Yr%.
        let deals = r.avg_deals_per_year_periods ?? _pa.avg_deals_per_year ?? null;
        if (deals == null) {
          const s0 = (p.start_time || '').slice(0, 10);
          const e0 = (p.stop_time  || '').slice(0, 10);
          const days = (s0 && e0) ? (new Date(e0) - new Date(s0)) / 86400000 : 0;
          if (days > 0) {
            deals = ((r.buy_count || 0) + (r.sell_count || 0)) * 365 / days;
          }
        }
        return (
          <td style={{...td, background: sortCol === 'avg_deals_per_year_periods' ? '#EFF6FF' : undefined}}>
            {deals != null
              ? <span style={{ fontWeight: 600, color: C.textMd }}>{fmtNum(deals, 2)}</span>
              : '—'}
          </td>
        );
      })()}
      {visibleColumns.periods_count && (() => {
        // Головне вікно не рахується періодом (воно зміщене за побудовою і в
        // підсумки не входить) — інакше пошук на 13 вікнах писав би 14.
        const _pr = _prArr;
        // Рахуються ЛИШЕ враховані вікна: без головного і без відкріплених
        // блоків. Число береться з колонки — тієї самої, за якою сортує сервер,
        // інакше клітинка й сортування розійшлись би.
        const n = r.periods_included_count != null
          ? r.periods_included_count
          : (_ps ? _ps.included
                 : _pr.filter(p => p && !p.is_main && p.included !== false).length);
        const nOff = _ps ? _ps.off
                         : _pr.filter(p => p && !p.is_main && p.included === false).length;
        const nTotal = _ps ? _ps.total : _pr.length;
        // Кнопка лишається живою і при нулі виміряних вікон: попап — це ще й
        // єдине місце, звідки період ДОДАЮТЬ, тож рядку без перевірок вона
        // потрібна найбільше.
        return (
          <td style={{ ...td, textAlign: 'center' }}>
            {nTotal > 0 ? (
              <button
                onClick={e => { e.stopPropagation(); onOpenPeriods && onOpenPeriods(r); }}
                style={{
                  fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 99,
                  cursor: 'pointer', background: '#EFF6FF', color: '#1E40AF',
                  border: '1px solid #DBEAFE',
                }}
                title={nOff > 0
                  ? `Враховано ${n} вікон, ще ${nOff} відкріплено (не входять у підсумок)`
                  : n > 0 ? 'Переглянути / додати періоди'
                          : 'Перевірочних вікон ще немає — відкрити й додати'}
              >
                📊 {n}{nOff > 0 && <span style={{ color: C.textSm, fontWeight: 400 }}> +{nOff}</span>}
              </button>
            ) : (
              <span style={{ color: C.textSm, fontSize: 11 }}>—</span>
            )}
          </td>
        );
      })()}

      {visibleColumns.sharpe   && <td style={td}>{eff.sharpe_ratio != null ? fmtNum(eff.sharpe_ratio, 3) : '—'}</td>}
      {visibleColumns.maxdd    && <td style={td}>{eff.max_drawdown != null ? fmtNum(eff.max_drawdown) + '%' : '—'}</td>}

      {visibleColumns.prof_months && (
        <td style={td} title="% місяців з позитивною доходністю (80%+ = стратегія стабільна в часі)">
          {eff.profitable_months_pct != null
            ? <span style={{ color: eff.profitable_months_pct >= 75 ? '#16A34A' : eff.profitable_months_pct >= 50 ? '#D97706' : '#DC2626', fontWeight: 600 }}>
                {fmtNum(eff.profitable_months_pct, 1) + '%'}
              </span>
            : '—'}
        </td>
      )}
      {visibleColumns.worst_month && (
        <td style={td} title="Найгірший місяць (чим менша втрата — тим краще)">
          {eff.worst_month != null
            ? <span style={{ color: eff.worst_month >= 0 ? '#16A34A' : eff.worst_month >= -3 ? '#D97706' : '#DC2626', fontWeight: 600 }}>
                {(eff.worst_month >= 0 ? '+' : '') + fmtNum(eff.worst_month, 2) + '%'}
              </span>
            : '—'}
        </td>
      )}
      {visibleColumns.calmar && (
        <td style={td} title="Calmar = Monthly% / MaxDD (вище = краще співвідношення дохід/ризик)">
          {eff.calmar_ratio != null
            ? <span style={{ color: eff.calmar_ratio >= 0.5 ? '#16A34A' : eff.calmar_ratio >= 0.2 ? '#D97706' : '#DC2626', fontWeight: 600 }}>
                {fmtNum(eff.calmar_ratio, 3)}
              </span>
            : '—'}
        </td>
      )}
      {visibleColumns.overnight && (
        <td style={{ ...td, textAlign: 'center' }}>
          {r.params?.risk_protection?.enabled && r.params.risk_protection.sell_overnight
            ? <span style={{ color: C.success, fontWeight: 700 }}>✓</span>
            : <span style={{ color: C.textSm }}>—</span>}
        </td>
      )}
      {visibleColumns.weekend  && (
        <td style={{ ...td, textAlign: 'center' }}>
          {r.params?.risk_protection?.enabled && r.params.risk_protection.sell_weekend
            ? <span style={{ color: C.success, fontWeight: 700 }}>✓</span>
            : <span style={{ color: C.textSm }}>—</span>}
        </td>
      )}
      {visibleColumns.max_loss && (
        <td style={{ ...td, textAlign: 'center' }}>
          {r.params?.risk_protection?.enabled
            ? <span>{r.params.risk_protection.max_loss_to_sell_pct}%</span>
            : <span style={{ color: C.textSm }}>—</span>}
        </td>
      )}

      {visibleColumns.sell_mode && (
        <td style={td}>
          {p.sell_mode === 'from_buy'
            ? <span style={{ display: 'inline-block', padding: '2px 7px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: '#FEE2E2', color: '#991B1B' }}>FromBuy</span>
            : <span style={{ display: 'inline-block', padding: '2px 7px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: '#DCFCE7', color: '#166534' }}>Rolling</span>}
        </td>
      )}

      {visibleColumns.stagnation && (
        <td style={{ ...td, textAlign: 'center' }}>
          {p.stagnation_enabled
            ? <span style={{ fontSize: 11, color: C.primary, fontWeight: 600 }}>
                ✓ {p.stagnation_min_pct}%/{p.stagnation_max_pct}%/{p.stagnation_bars}b
              </span>
            : <span style={{ color: C.textSm }}>—</span>}
        </td>
      )}

      {visibleColumns.reinvest && (
        <td style={{ ...td, textAlign: 'center' }}>
          {p.reinvest === 'protected'
            ? <span style={{ fontSize: 11, color: '#f59e0b', fontWeight: 600 }}>Protected</span>
            : p.reinvest === false
              ? <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>Fixed</span>
              : <span style={{ fontSize: 11, color: C.success, fontWeight: 600 }}>Cash</span>}
        </td>
      )}

      {visibleColumns.hard_stop && (
        <td style={{ ...td, textAlign: 'center' }}>
          {p.hard_stop_pct > 0
            ? <span style={{ fontSize: 11, color: '#ef4444', fontWeight: 600 }}>
                {p.hard_stop_pct}%
                {p.hard_stop_cooldown_bars > 0 && (
                  <span style={{ fontSize: 10, color: C.textSm, fontWeight: 400, marginLeft: 3 }}>
                    CD{p.hard_stop_cooldown_bars}b
                  </span>
                )}
              </span>
            : <span style={{ color: C.textSm }}>—</span>}
        </td>
      )}

      {visibleColumns.peak_window && (
        <td style={{ ...td, textAlign: 'center' }}>
          {(p.use_peak_window_buy || p.use_peak_window_sell)
            ? <span style={{ fontSize: 11, fontWeight: 600, color: C.primary }}>
                {p.use_peak_window_buy  && <span title="BUY: min in window">B</span>}
                {p.use_peak_window_buy && p.use_peak_window_sell && '+'}
                {p.use_peak_window_sell && <span title="SELL: max in window">S</span>}
              </span>
            : <span style={{ color: C.textSm }}>—</span>}
        </td>
      )}

      {visibleColumns.buy_cap && (
        <td style={{ ...td, textAlign: 'center' }}>
          {p.buy_cap_enabled
            ? <span style={{ fontSize: 11, color: '#0369A1', fontWeight: 600 }}>≤{p.buy_cap_pct}%</span>
            : <span style={{ color: C.textSm }}>—</span>}
        </td>
      )}
      {visibleColumns.overpriced && (
        <td style={{ ...td, textAlign: 'center' }}>
          {p.overpriced_filter_enabled
            ? <span style={{ fontSize: 11, color: '#7C3AED', fontWeight: 600 }}>{p.overpriced_pct}%/{p.overpriced_period_bars}b</span>
            : <span style={{ color: C.textSm }}>—</span>}
        </td>
      )}
      {/* Dip і Take Profit — по дві колонки (менеджер 26.08): склеєне
          «45%/50b» сортувалось лише цілком, а шукати треба окремо за глибиною
          падіння і окремо за довжиною вікна. */}
      {visibleColumns.dip_pct && (
        <td style={{ ...td, textAlign: 'center' }}>
          {p.dip_filter_enabled
            ? <span style={{ fontSize: 11, color: C.success, fontWeight: 600 }}>{p.dip_pct}%</span>
            : <span style={{ color: C.textSm }}>—</span>}
        </td>
      )}
      {visibleColumns.dip_bars && (
        <td style={{ ...td, textAlign: 'center' }}>
          {p.dip_filter_enabled
            ? <span style={{ fontSize: 11, color: C.success, fontWeight: 600 }}>{p.dip_period_bars}b</span>
            : <span style={{ color: C.textSm }}>—</span>}
        </td>
      )}
      {visibleColumns.rotation && (
        <td style={{ ...td, textAlign: 'center' }}>
          {p.rotation_enabled
            ? <span style={{ fontSize: 11, color: '#D97706', fontWeight: 600 }}>{p.rotation_min_hold_bars}b×{p.rotation_factor}</span>
            : <span style={{ color: C.textSm }}>—</span>}
        </td>
      )}
      {visibleColumns.opportunity && (
        <td style={{ ...td, textAlign: 'center' }}>
          {p.opportunity_enabled
            ? <span style={{ fontSize: 11, color: '#0891B2', fontWeight: 600 }}>{p.opportunity_min_hold_bars}b+{p.opportunity_min_gain_pct}%</span>
            : <span style={{ color: C.textSm }}>—</span>}
        </td>
      )}
      {visibleColumns.hold_to_cost && (
        <td style={{ ...td, textAlign: 'center' }}>
          {p.hold_to_cost_enabled
            ? <span style={{ fontSize: 11, color: '#DC2626', fontWeight: 600 }}>≤{p.hold_to_cost_bars}b</span>
            : <span style={{ color: C.textSm }}>—</span>}
        </td>
      )}
      {visibleColumns.htc_cost && (
        <td style={{ ...td, textAlign: 'center' }}
            title="Hold to Cost від беззбитковості: комісії купівлі й продажу + спред + націнка">
          {p.hold_to_cost_enabled && (p.htc_cost_enabled === true || p.htc_cost_enabled === 'true')
            ? <span style={{ fontSize: 11, color: '#059669', fontWeight: 600 }}>+{p.htc_cost_markup_pct ?? 0.1}%</span>
            : <span style={{ color: C.textSm }}>—</span>}
        </td>
      )}
      {visibleColumns.swap_defer && (
        <td style={{ ...td, textAlign: 'center' }}>
          {p.swap_defer_enabled
            ? <span style={{ fontSize: 11, color: '#7C3AED', fontWeight: 600 }}>✓</span>
            : <span style={{ color: C.textSm }}>—</span>}
        </td>
      )}
      {visibleColumns.swap_tp_frac && (
        <td style={{ ...td, textAlign: 'center' }}>
          {Number(p.swap_tp_frac_pct || 0) > 0
            ? <span style={{ fontSize: 11, color: '#7C3AED', fontWeight: 600 }}>{p.swap_tp_frac_pct}%</span>
            : <span style={{ color: C.textSm }}>—</span>}
        </td>
      )}
      {visibleColumns.swap_htc && (
        <td style={{ ...td, textAlign: 'center' }}>
          {p.swap_htc_enabled
            ? <span style={{ fontSize: 11, color: '#7C3AED', fontWeight: 600 }}>✓</span>
            : <span style={{ color: C.textSm }}>—</span>}
        </td>
      )}
      {visibleColumns.no_growth && (
        <td style={{ ...td, textAlign: 'center' }}
            title="Buy% на виході: «утрим.» — продаж, якщо після Sell Window ціна < купівлі + Buy%; «вхід» — лише ініціює купівлю">
          {(p.strategy || '') !== 'advanced'
            ? <span style={{ color: C.textSm }}>—</span>
            : (p.no_growth_enabled === false || p.no_growth_enabled === 'false')
              ? <span style={{ fontSize: 11, color: '#7C3AED', fontWeight: 600 }}>вхід</span>
              : <span style={{ fontSize: 11, color: C.textMd }}>утрим.</span>}
        </td>
      )}
      {visibleColumns.smart_gen && (
        <td style={{ ...td, textAlign: 'center' }}
            title={p.smart_enabled ? 'Знайдено смарт-пошуком у цьому поколінні' : 'Звичайний перебір'}>
          {p.smart_enabled && p.smart_gen
            ? <span style={{ fontSize: 11, color: '#0F766E', fontWeight: 600 }}>{p.smart_gen}</span>
            : <span style={{ color: C.textSm }}>—</span>}
        </td>
      )}
      {visibleColumns.tp_pct && (
        <td style={{ ...td, textAlign: 'center' }}>
          {p.take_profit_mode > 0
            ? <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <span style={{
                  fontSize: 11, fontWeight: 700,
                  color: p.take_profit_mode === 1 ? '#B45309' : '#5B21B6',
                }}>
                  +{p.take_profit_pct}%
                </span>
                {/* Значок режиму лишається тут: «+30%» без нього не читається,
                    а окрема колонка «Sell immed.» потрібна для сортування. */}
                <span style={{
                  fontSize: 10, fontWeight: 600, padding: '1px 5px', borderRadius: 3,
                  background: p.take_profit_mode === 1 ? '#FEF3C7' : '#EDE9FE',
                  color:      p.take_profit_mode === 1 ? '#92400E' : '#5B21B6',
                }}>
                  {p.take_profit_mode === 1 ? '⚡ Sell' : '🔓 Hold'}
                </span>
              </span>
            : <span style={{ color: C.textSm }}>—</span>}
        </td>
      )}
      {visibleColumns.tp_bars && (
        <td style={{ ...td, textAlign: 'center' }}>
          {p.take_profit_mode > 0 && p.take_profit_expiry_bars > 0
            ? <span style={{ fontSize: 11, color: '#5B21B6', fontWeight: 600 }}>{p.take_profit_expiry_bars}b</span>
            : <span style={{ color: C.textSm }}
                    title={p.take_profit_mode > 0 ? 'Без обмеження за часом' : undefined}>—</span>}
        </td>
      )}

      {visibleColumns.tp_sell_immediately && (
        <td style={{ ...td, textAlign: 'center' }}>
          {p.take_profit_mode > 0
            ? p.take_profit_mode === 1
              ? <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 4, background: '#FEF3C7', color: '#92400E' }}>Yes</span>
              : <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 4, background: '#EDE9FE', color: '#5B21B6' }}>No</span>
            : <span style={{ color: C.textSm }}>—</span>}
        </td>
      )}

      {/* ── Шари реалізму (блоки 5-10 форми) ──────────────────────────────────
          Прочерк = шар вимкнений, тобто рядок рахований старими правилами.
          Для увімкнених показуємо не «✓», а самі числа: два рядки з обігом
          і різним лімітом заявки — це різні прогони, і в таблиці це має бути
          видно без заходу в деталі. */}
      {visibleColumns.liquidity && (
        <td style={{ ...td, textAlign: 'center' }}>
          {p.liquidity_enabled
            ? <span style={{ fontSize: 11, color: '#0369A1', fontWeight: 600 }}
                    title={`Мін. обіг $${Number(p.min_adv_usd || 0).toLocaleString('en-US')}/день · ліміт заявки ${p.position_adv_cap_pct}% · вікно ${p.adv_window_days}д · порожніх барів ≤${p.max_flat_bar_pct}%`}>
                {p.position_adv_cap_pct}%/{(Number(p.min_adv_usd || 0) / 1e6).toFixed(2)}M
              </span>
            : <span style={{ color: C.textSm }}>—</span>}
        </td>
      )}
      {visibleColumns.impact && (
        <td style={{ ...td, textAlign: 'center' }}>
          {p.liquidity_enabled && p.impact_enabled
            ? <span style={{ fontSize: 11, color: '#7C3AED', fontWeight: 600 }}>{p.impact_bps_per_pct} б.п.</span>
            : <span style={{ color: C.textSm }}>—</span>}
        </td>
      )}
      {visibleColumns.margin && (
        <td style={{ ...td, textAlign: 'center' }}>
          {p.margin_enabled
            ? <span style={{ fontSize: 11, color: '#DC2626', fontWeight: 600 }}
                    title={`Підтримка ${p.margin_maintenance_pct}% · делеверидж ${p.margin_delev_start_pct}→${p.margin_delev_stop_pct}% · APR ${p.margin_interest_apr_pct}% · мін. ціна $${p.margin_min_price}`}>
                ×{p.margin_cap}
              </span>
            : <span style={{ color: C.textSm }}>—</span>}
        </td>
      )}
      {visibleColumns.pit_universe && (
        <td style={{ ...td, textAlign: 'center' }}>
          {p.pit_universe_enabled
            ? <span style={{ fontSize: 11, color: C.success, fontWeight: 600 }}
                    title={p.pit_require_data
                      ? 'Тікер без архівної капіталізації не є кандидатом'
                      : 'Тікер без архівної капіталізації судиться сьогоднішньою'}>
                {p.pit_require_data ? 'строго' : 'відкат'}
              </span>
            : <span style={{ color: C.textSm }}>—</span>}
        </td>
      )}
      {visibleColumns.delisting && (
        <td style={{ ...td, textAlign: 'center' }}>
          {p.delisting_enabled
            ? <span style={{ fontSize: 11, color: '#B45309', fontWeight: 600 }}>✓</span>
            : <span style={{ color: C.textSm }}>—</span>}
        </td>
      )}
      {visibleColumns.reorg && (
        <td style={{ ...td, textAlign: 'center' }}>
          {p.reorg_enabled
            ? <span style={{ fontSize: 11, color: '#B45309', fontWeight: 600 }}>✓</span>
            : <span style={{ color: C.textSm }}>—</span>}
        </td>
      )}
      {visibleColumns.per_bar_universe && (
        <td style={{ ...td, textAlign: 'center' }}>
          {p.per_bar_universe
            ? <span style={{ fontSize: 11, color: C.success, fontWeight: 600 }}>✓</span>
            : <span style={{ color: C.textSm }}>—</span>}
        </td>
      )}
      {visibleColumns.slicing && (
        <td style={{ ...td, textAlign: 'center' }}>
          {p.slicing_enabled
            ? <span style={{ fontSize: 11, color: '#0891B2', fontWeight: 600 }}
                    title={`Мін. частина $${p.min_position_usd} · мін. ціна акції $${p.min_share_price}`}>
                ≥${p.min_position_usd}
              </span>
            : <span style={{ color: C.textSm }}>—</span>}
        </td>
      )}
      {visibleColumns.partial_sells && (
        <td style={{ ...td, textAlign: 'center' }}>
          {p.partial_sells_enabled
            ? <span style={{ fontSize: 11, color: '#0891B2', fontWeight: 600 }}>✓</span>
            : <span style={{ color: C.textSm }}>—</span>}
        </td>
      )}
      {visibleColumns.dynamic_n && (
        <td style={{ ...td, textAlign: 'center' }}>
          {p.dynamic_n_enabled
            ? <span style={{ fontSize: 11, color: '#D97706', fontWeight: 600 }}
                    title={Number(p.dynamic_n_mode) === 1
                      ? 'База ділення — лише вартість акцій'
                      : 'База ділення — акції + кеш'}>
                {Number(p.dynamic_n_mode) === 1 ? 'акції' : 'акції+кеш'}
              </span>
            : <span style={{ color: C.textSm }}>—</span>}
        </td>
      )}

      {/* Пріоритет черги купівлі і набір капіталізації — осі перебору
          (менеджер 2026-08-19). Беруться з ПАРАМЕТРІВ РЯДКА, а не з мети
          батчу: в одному пошуку рядки мають різні значення. */}
      {visibleColumns.buy_priority && (
        <td style={{ ...td, textAlign: 'center' }}>
          {(() => {
            const bp = p.buy_priority || 'dip';
            const lbl = { dip: 'падіння', turnover: 'обіг', spike: 'сплеск' }[bp] || bp;
            return <span style={{ fontSize: 11, color: bp === 'dip' ? C.textSm : '#0369A1',
                                  fontWeight: bp === 'dip' ? 400 : 600 }}>{lbl}</span>;
          })()}
        </td>
      )}
      {visibleColumns.cap_set && (
        <td style={{ ...td, textAlign: 'center' }}>
          {Array.isArray(p.market_cap_buckets) && p.market_cap_buckets.length
            ? <span style={{ fontSize: 10 }}>{rm.market_cap_type}</span>
            : <span style={{ color: C.textSm }}>—</span>}
        </td>
      )}
      {visibleColumns.regime     && <td style={{ ...td, textAlign: 'center', width: 50 }} title={metaTooltip(rm)}>{renderRegime(rm)}</td>}
      {visibleColumns.tickers    && <td style={{ ...td, width: 130 }} title={metaTooltip(rm)}>{renderTickers(rm)}</td>}
      {visibleColumns.sector     && <td style={{ ...td, width: 100 }} title={metaTooltip(rm)}>{renderSector(rm)}</td>}
      {visibleColumns.market_cap && <td style={{ ...td, width: 70, textAlign: 'center' }} title={metaTooltip(rm)}>{renderMarketCap(rm)}</td>}
      {visibleColumns.country    && <td style={{ ...td, width: 70 }} title={metaTooltip(rm)}>{renderCountry(rm)}</td>}

      {/* ── Period columns (2 <td> per period: monthly + yearly) ── */}
      {periodDefs.map((period, i) => {
        const prList = r._meta?.period_results || [];
        const pr = prList.find(p => p.start === period.start);
        const borderLeft = i === 0 ? '2px solid #86EFAC' : undefined;
        if (!pr) {
          return [
            <td key={`p${i}mo`} style={{ ...td, background: '#F7FEF7', textAlign: 'center', borderLeft }}>
              <span style={{ color: C.textSm, fontSize: 10 }}>—</span>
            </td>,
            <td key={`p${i}yr`} style={{ ...td, background: '#F7FEF7', textAlign: 'center' }}>
              <span style={{ color: C.textSm, fontSize: 10 }}>—</span>
            </td>,
          ];
        }
        if (pr.error) {
          return [
            <td key={`p${i}mo`} style={{ ...td, background: '#FFF5F5', textAlign: 'center', borderLeft }}>
              <span style={{ color: C.danger, fontSize: 10 }}>✗</span>
            </td>,
            <td key={`p${i}yr`} style={{ ...td, background: '#FFF5F5', textAlign: 'center' }}>
              <span style={{ color: C.danger, fontSize: 10 }}>✗</span>
            </td>,
          ];
        }
        const tooltip = `P${i+1}: ${period.start}–${period.end} (${period.days}d)\nTotal: ${pr.total_return >= 0 ? '+' : ''}${pr.total_return?.toFixed(2)}%\nMonthly: ${pr.monthly_return >= 0 ? '+' : ''}${pr.monthly_return?.toFixed(2)}%/mo\nYearly: ${pr.yearly_return >= 0 ? '+' : ''}${pr.yearly_return?.toFixed(1)}%/yr\nWin: ${pr.win_rate?.toFixed(0)}%`;
        const moColor = pr.monthly_return >= 0 ? C.success : C.danger;
        const yrColor = pr.yearly_return >= 0 ? C.success : C.danger;
        return [
          <td key={`p${i}mo`} style={{ ...td, background: '#F0FDF4', textAlign: 'center', borderLeft }} title={tooltip}>
            <span style={{ fontWeight: 700, fontSize: 12, color: moColor }}>
              {fmtAnnualPct(pr.monthly_return, 1)}
            </span>
          </td>,
          <td key={`p${i}yr`} style={{ ...td, background: '#F0FDF4', textAlign: 'center' }} title={tooltip}>
            <span style={{ fontWeight: 600, fontSize: 12, color: yrColor }}>
              {fmtAnnualPct(pr.yearly_return, 1)}
            </span>
          </td>,
        ];
      })}

      {/* ── Chain summary columns (2 <td>: monthly + yearly) ── */}
      {hasChainSummary && (() => {
        const cs = r._meta?.period_chain_summary;
        if (!cs) return [
          <td key="chainmo" style={{ ...td, background: '#FAF5FF', textAlign: 'center', borderLeft: '2px solid #A78BFA' }}>
            <span style={{ color: C.textSm, fontSize: 10 }}>—</span>
          </td>,
          <td key="chainyr" style={{ ...td, background: '#FAF5FF', textAlign: 'center' }}>
            <span style={{ color: C.textSm, fontSize: 10 }}>—</span>
          </td>,
        ];
        const tooltip = `⛓ Chained result (${cs.total_days} days)\n$${cs.initial_capital?.toLocaleString()} → $${cs.final_capital?.toLocaleString()}\nTotal: ${cs.total_return >= 0 ? '+' : ''}${cs.total_return?.toFixed(2)}%\nMonthly: ${cs.monthly_return >= 0 ? '+' : ''}${cs.monthly_return?.toFixed(2)}%/mo\nYearly: ${cs.yearly_return >= 0 ? '+' : ''}${cs.yearly_return?.toFixed(1)}%/yr`;
        const moColor = cs.monthly_return >= 0 ? '#7C3AED' : C.danger;
        const yrColor = cs.yearly_return >= 0 ? '#6D28D9' : C.danger;
        return [
          <td key="chainmo" style={{ ...td, background: '#F5F3FF', textAlign: 'center', borderLeft: '2px solid #A78BFA' }} title={tooltip}>
            <span style={{ fontWeight: 700, fontSize: 12, color: moColor }}>
              {fmtAnnualPct(cs.monthly_return, 1)}
            </span>
          </td>,
          <td key="chainyr" style={{ ...td, background: '#F5F3FF', textAlign: 'center' }} title={tooltip}>
            <span style={{ fontWeight: 600, fontSize: 12, color: yrColor }}>
              {fmtAnnualPct(cs.yearly_return, 1)}
            </span>
          </td>,
        ];
      })()}

      <td style={td}>
        <span
          style={badge(isRunning ? 'none' : r.status === 'completed' ? 'green' : 'red')}
          title={r.status === 'failed' ? (r.error_detail || 'Failed') : undefined}
        >
          {isRunning ? '⏳' : r.status === 'completed' ? '✓' : '✗'}
        </span>
      </td>

      {/* ── Mock status cell (окремий стовпець) ── */}
      <td style={{ ...td, textAlign: 'center', padding: '8px 6px' }} onClick={e => e.stopPropagation()}>
        {isMockRunning ? (
          <button
            title="Запущена на мокових даних — перейти до Running"
            style={{
              padding: '3px 9px', borderRadius: 5, fontSize: 11, fontWeight: 700,
              cursor: 'pointer', border: '1px solid #86EFAC',
              background: '#DCFCE7', color: '#15803D',
              whiteSpace: 'nowrap',
            }}
            onClick={() => onRunMock && onRunMock(r, true)}
          >● Running</button>
        ) : (
          <button
            title="Запустити стратегію на мокових даних"
            style={{
              padding: '3px 9px', borderRadius: 5, fontSize: 11, fontWeight: 600,
              cursor: isRunning ? 'default' : 'pointer',
              border: '1px solid #BAE6FD', background: '#F0F9FF', color: '#0369A1',
              opacity: isRunning ? 0.5 : 1,
              whiteSpace: 'nowrap',
            }}
            disabled={isRunning}
            onClick={() => onRunMock && onRunMock(r, false)}
          >▶ Start</button>
        )}
      </td>

      {/* ── Actions ── */}
      <td style={td} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', gap: 4 }}>
          <button title="View" style={{ ...btnSm }} disabled={isRunning} onClick={() => onView(r)}>▶</button>
          <button title="Copy params" style={{ ...btnSm, color: C.warning, borderColor: C.warning }} disabled={isRunning} onClick={() => onCopy(r)}>📋</button>
          <button title="Recalculate" style={btnSm} disabled={isRunning || recalcingId === r.id} onClick={() => onRecalc(r.id)}>
            {recalcingId === r.id ? '⏳' : '🔄'}
          </button>
          <button title="Delete" style={{ ...btnSm, color: C.danger, borderColor: C.danger }} onClick={() => onDelete(r.id)}>🗑</button>
        </div>
      </td>
    </tr>
  );
}
